/**
 * useFormDrafts - Manage multiple auto-saved drafts per template
 *
 * Extends auto-save to support multiple concurrent drafts.
 * Each new form gets a unique instance ID, allowing users to have
 * multiple in-progress forms of the same template.
 */

import { useCallback, useEffect, useState } from 'react';
import { QUOTE_WIZARD_DRAFT_PREFIX } from './quotes/useQuoteWizardAutoSave';
import { EXPLICIT_SAVE_PREFIX } from '../lib/forms/explicitSaveLedger';

// ============================================================================
// Types
// ============================================================================

export interface FormDraftMetadata {
  /** Unique instance ID for this draft */
  id: string;
  /** Template this draft belongs to */
  templateId: string;
  /** When this draft was last saved */
  savedAt: number;
  /** Preview text for UI (e.g., "Customer: ABC Corp") */
  preview: string;
  /** Form values */
  values: Record<string, unknown>;
}

interface DraftIndex {
  drafts: FormDraftMetadata[];
  updatedAt: number;
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_PREFIX = 'form-autosave:';
const INDEX_PREFIX = 'form-drafts-index:';
/** Mirrors FILL_SESSION_PREFIX in lib/forms/fillSession.ts — duplicated rather
 *  than imported to keep this module free of a dependency on the forms lib. The
 *  test below asserts the two stay in step. */
const FILL_SESSION_PREFIX = 'form-fill-session:';
const MAX_DRAFTS_PER_TEMPLATE = 10; // Prevent localStorage bloat
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// ============================================================================
// Helpers
// ============================================================================

function generateInstanceId(): string {
  return `instance-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Wipe every locally-autosaved form draft on this device.
 *
 * Called ONLY when a DIFFERENT user signs in on the same device — never on a same-user
 * logout/login, which would destroy that person's own resumable work. Local drafts live
 * only in localStorage (no Firestore copy), so clearing them is irreversible.
 *
 * Why it is needed: the draft index is keyed by templateId alone, so the picker in
 * FormFiller shows every locally-saved draft for a template regardless of who created
 * it — and localStorage survives sign-out. On a shared field tablet that leaves one
 * technician's in-progress content (customer, site, readings, preview text) sitting in
 * front of the next person to log in. The Firestore offline cache already gets wiped on
 * this same transition; these keys were simply missed.
 */
export function clearLocalFormDrafts(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith(STORAGE_PREFIX) ||
          key.startsWith(INDEX_PREFIX) ||
          // Fill-session identity (lib/forms/fillSession.ts). Added here rather
          // than left out: this wipe exists so one technician's in-progress work
          // cannot outlive their session on a shared field tablet, and a new
          // localStorage prefix that escapes it quietly narrows that guarantee.
          key.startsWith(FILL_SESSION_PREFIX) ||
          // The device's explicit-save counts (T4c, lib/forms/explicitSaveLedger.ts): per-draft
          // state of the technician whose drafts are being wiped.
          key.startsWith(EXPLICIT_SAVE_PREFIX) ||
          // Quote-wizard drafts (#3924): org- or quote-keyed, not user-keyed, so the
          // previous rep's customer, line items and pricing would otherwise survive.
          key.startsWith(QUOTE_WIZARD_DRAFT_PREFIX))
      ) {
        doomed.push(key);
      }
    }
    // Collected first, removed after — removing during iteration reindexes the store
    // and silently skips keys.
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch {
    // localStorage unavailable (private mode). Nothing was stored, nothing to clear.
  }
}

function buildDraftKey(templateId: string, instanceId: string): string {
  return `${STORAGE_PREFIX}${templateId}:${instanceId}`;
}

function buildIndexKey(templateId: string): string {
  return `${INDEX_PREFIX}${templateId}`;
}

/**
 * Generate preview text from form values
 */
function generatePreview(values: Record<string, unknown>): string {
  // Try common field names for preview
  const customer = values.customer as string | undefined;
  const jobNumber = values.jobNumber as string | undefined;
  const name = values.name as string | undefined;

  const parts: string[] = [];
  if (customer) parts.push(`Customer: ${customer}`);
  if (jobNumber) parts.push(`Job: ${jobNumber}`);
  if (name) parts.push(name);

  return parts.length > 0 ? parts.join(' • ') : 'Untitled form';
}

/**
 * Load draft index for a template
 */
function loadIndex(templateId: string): DraftIndex {
  try {
    const raw = localStorage.getItem(buildIndexKey(templateId));
    if (!raw) return { drafts: [], updatedAt: Date.now() };

    const parsed: DraftIndex = JSON.parse(raw);

    // Filter out expired drafts
    const now = Date.now();
    const validDrafts = parsed.drafts.filter((d) => now - d.savedAt < MAX_AGE_MS);

    // Clean up expired draft storage
    const expiredDrafts = parsed.drafts.filter((d) => now - d.savedAt >= MAX_AGE_MS);
    expiredDrafts.forEach((d) => {
      localStorage.removeItem(buildDraftKey(templateId, d.id));
    });

    return { drafts: validDrafts, updatedAt: now };
  } catch {
    return { drafts: [], updatedAt: Date.now() };
  }
}

/**
 * Save draft index
 */
function saveIndex(templateId: string, index: DraftIndex): void {
  try {
    localStorage.setItem(buildIndexKey(templateId), JSON.stringify(index));
  } catch (err) {
    console.warn('Failed to save draft index:', err);
  }
}

/**
 * Remove expired drafts (and their stored payloads) for a template, then persist the
 * pruned index. `loadIndex` already deletes expired draft storage as a side effect;
 * writing the index back makes the pruning durable. Safe to call from autosave on a
 * localStorage quota error to reclaim space — it never touches live (unexpired)
 * drafts, so it cannot lose another in-progress form's work.
 */
export function pruneExpiredDrafts(templateId: string | undefined): void {
  if (!templateId) return;
  const index = loadIndex(templateId);
  saveIndex(templateId, index);
}

// ============================================================================
// Hook
// ============================================================================

export function useFormDrafts(templateId: string | undefined) {
  const [drafts, setDrafts] = useState<FormDraftMetadata[]>([]);

  // Load drafts on mount
  useEffect(() => {
    if (!templateId) return;

    const index = loadIndex(templateId);
    setDrafts(index.drafts);
  }, [templateId]);

  /**
   * Save a draft (called by auto-save)
   */
  const saveDraft = useCallback(
    (instanceId: string, values: Record<string, unknown>) => {
      if (!templateId) return;

      const metadata: FormDraftMetadata = {
        id: instanceId,
        templateId,
        savedAt: Date.now(),
        preview: generatePreview(values),
        values,
      };

      // Save the draft data
      try {
        localStorage.setItem(buildDraftKey(templateId, instanceId), JSON.stringify(metadata));
      } catch (err) {
        console.warn('Failed to save draft:', err);
        return;
      }

      // Update index
      const index = loadIndex(templateId);
      const existingIndex = index.drafts.findIndex((d) => d.id === instanceId);

      if (existingIndex >= 0) {
        // Update existing draft
        index.drafts[existingIndex] = metadata;
      } else {
        // Add new draft
        index.drafts.unshift(metadata);

        // Enforce max drafts limit (remove oldest)
        if (index.drafts.length > MAX_DRAFTS_PER_TEMPLATE) {
          const removed = index.drafts.splice(MAX_DRAFTS_PER_TEMPLATE);
          removed.forEach((d) => {
            localStorage.removeItem(buildDraftKey(templateId, d.id));
          });
        }
      }

      index.updatedAt = Date.now();
      saveIndex(templateId, index);
      setDrafts([...index.drafts]);
    },
    [templateId]
  );

  /**
   * Delete a draft
   */
  const deleteDraft = useCallback(
    (instanceId: string) => {
      if (!templateId) return;

      localStorage.removeItem(buildDraftKey(templateId, instanceId));

      const index = loadIndex(templateId);
      index.drafts = index.drafts.filter((d) => d.id !== instanceId);
      index.updatedAt = Date.now();
      saveIndex(templateId, index);
      setDrafts([...index.drafts]);
    },
    [templateId]
  );

  /**
   * Load a specific draft
   */
  const loadDraft = useCallback(
    (instanceId: string): FormDraftMetadata | null => {
      if (!templateId) return null;

      try {
        const raw = localStorage.getItem(buildDraftKey(templateId, instanceId));
        if (!raw) return null;

        return JSON.parse(raw);
      } catch {
        return null;
      }
    },
    [templateId]
  );

  /**
   * Delete all drafts for this template
   */
  const clearAllDrafts = useCallback(() => {
    if (!templateId) return;

    const index = loadIndex(templateId);
    index.drafts.forEach((d) => {
      localStorage.removeItem(buildDraftKey(templateId, d.id));
    });

    localStorage.removeItem(buildIndexKey(templateId));
    setDrafts([]);
  }, [templateId]);

  return {
    drafts,
    saveDraft,
    deleteDraft,
    loadDraft,
    clearAllDrafts,
    generateInstanceId,
  };
}
